import React from 'react'
import * as Sentry from '@sentry/react'
import { AxiosError } from 'axios'
import config from './config'
import './forms/validation'
import { AppRoutes } from './navigation/root/AppRoutes'
import AppTranslationProvider from './hooks/useAppTranslation'
import { useWebsocket } from './hooks/useWebsocket'
import { ToastNotificationProvider } from './hooks/useToastNotification'
import { isDevEnv } from './helpers/environment'
import { AuthProvider } from './hooks/useAuth'
import { useBlockedSocietyIds } from './hooks/useBlockedSocietyIds'
import { SelectedChatIdProvider } from './state/context/SelectedChatIdProvider'

const PROD_BASE_URL = 'https://app.boappa.se'

// DEV: a url is the one part of a page that gets written down everywhere --
//      browser history, referrer headers, and the errors, traces and
//      breadcrumbs that go to Sentry. Two of ours carry something that should
//      not be written down anywhere: the password link arrives as ?token= and
//      is enough on its own to sign in as its owner, and signup used to pass
//      the address it was handed as ?email=. Anything named here is replaced
//      before an event leaves.
const SENSITIVE_QUERY_KEYS = ['token', 'code', 'email']

const scrubUrl = (url?: string): string | undefined => {
  if (url == null || url.indexOf('?') === -1) {
    return url
  }

  try {
    // DEV: a base only so that relative urls parse -- breadcrumbs carry paths
    //      rather than whole urls. It is dropped again below.
    const parsed = new URL(url, PROD_BASE_URL)
    const found = SENSITIVE_QUERY_KEYS.filter((key) =>
      parsed.searchParams.has(key)
    )

    if (found.length === 0) {
      return url
    }

    found.forEach((key) => parsed.searchParams.set(key, '[Filtered]'))

    return url.startsWith('http')
      ? parsed.toString()
      : `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    // DEV: not something we can parse, so drop the whole query rather than
    //      guess at which part of it was the secret
    return url.split('?')[0]
  }
}

// DEV: a new object rather than a write through the parameter, which airbnb
//      forbids and Sentry has no reason to expect. scrubUrl hands back the very
//      string it was given when there was nothing to replace, so the common
//      case -- almost every event -- copies nothing at all.
const scrubEventUrl = <T extends { request?: { url?: string } }>(
  event: T
): T => {
  const url = event.request?.url
  if (url == null) {
    return event
  }

  const scrubbed = scrubUrl(url)
  if (scrubbed === url) {
    return event
  }

  return { ...event, request: { ...event.request, url: scrubbed } } as T
}

Sentry.init({
  enabled: !isDevEnv(),
  dsn: config.SENTRY_DSN,
  debug: config.DEBUG,
  environment: config.SENTRY_ENVIRONMENT,
  release: import.meta.env.VITE_APP_RELEASE,
  tracesSampleRate: 0.1,
  // This sets the sample rate to be 10%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 0.1,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  beforeSend(event, hint) {
    if (isDevEnv()) {
      // eslint-disable-next-line no-console
      console.error(hint.originalException || hint.syntheticException)
      return null
    }
    if (hint && hint.originalException instanceof Error) {
      const axiosError = hint.originalException as AxiosError
      if (
        axiosError.isAxiosError &&
        ['Request aborted', 'timeout exceeded'].some(
          (el) => el === axiosError.message
        )
      ) {
        /* 
        We ignore these errors due to:
         - High volume, low impact
         - Possible transient network issues or app in background
         - No actionable data
         - Resource optimization and decluttering in Sentry 
        */
        return null
      }
    }
    return scrubEventUrl(event)
  },

  // DEV: errors are only one of the three places a url reaches Sentry. Traces
  //      carry the url of the page they measured, and breadcrumbs carry every
  //      navigation and request leading up to an event -- so all three are
  //      covered, or the link code simply arrives by another road.
  beforeSendTransaction(event) {
    return scrubEventUrl(event)
  },

  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.data == null) {
      return breadcrumb
    }

    const data: { [key: string]: unknown } = { ...breadcrumb.data }
    const urlKeys = ['url', 'from', 'to']

    for (let i = 0; i < urlKeys.length; i += 1) {
      const key = urlKeys[i]
      if (typeof data[key] === 'string') {
        data[key] = scrubUrl(data[key] as string)
      }
    }

    return { ...breadcrumb, data }
  },
})

const AppInitialized = (): JSX.Element | null => {
  useWebsocket()
  useBlockedSocietyIds()

  // Solution for societies that mask their society website under their own domain
  if (!isDevEnv() && window.location.origin !== PROD_BASE_URL) {
    window.location.href = PROD_BASE_URL
  }

  // Setup providers
  return <AppRoutes />
}

AppInitialized.displayName = 'AppInitialized'

export const App = (): JSX.Element | null => {
  // Setup providers
  return (
    <AuthProvider>
      <AppTranslationProvider>
        <ToastNotificationProvider>
          <SelectedChatIdProvider>
            <AppInitialized />
          </SelectedChatIdProvider>
        </ToastNotificationProvider>
      </AppTranslationProvider>
    </AuthProvider>
  )
}

export default App
